/**
 * @fileoverview ClubOS Finance — Expense Management
 * Track club expenses by category: Salaries, Equipment, Facility, Travel, etc.
 * Add, edit, approve, delete expenses. Filter by category, status, date.
 * PDF report includes audit trail (recorded by / on, updated by / on).
 */

import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import PropTypes from "prop-types";
import { financeService } from "api";
import FootballLoader from "components/FootballLoader";

const CATEGORIES = [
  "Salaries", "Equipment", "Facility", "Travel", "Medical",
  "Scouting", "Academy", "Marketing", "Utilities", "Other",
];

const PAYMENT_METHODS = ["Cash", "Bank Transfer", "Cheque", "UPI", "Credit Card", "Petty Cash"];

const STATUS_COLOR = {
  Draft: "secondary",
  Pending: "warning",
  Approved: "success",
  Rejected: "error",
  Paid: "info",
};

const CATEGORY_ICON = {
  Salaries: "payments",
  Equipment: "sports_soccer",
  Facility: "home",
  Travel: "directions_bus",
  Medical: "local_hospital",
  Scouting: "radar",
  Academy: "school",
  Marketing: "campaign",
  Utilities: "electrical_services",
  Other: "receipt_long",
};

const currentYear  = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const emptyForm = {
  title: "", category: "Equipment", amount: "", paymentMethod: "Bank Transfer",
  expenseDate: new Date().toISOString().slice(0, 10),
  vendor: "", invoiceRef: "", description: "", status: "Draft",
};

// ── Validate ─────────────────────────────────────────────────────────────────
function validateExpense(form) {
  const e = {};
  if (!form.title.trim())   e.title = "Title is required.";
  if (!form.category)       e.category = "Category is required.";
  if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0)
    e.amount = "Enter a valid amount.";
  if (!form.expenseDate)    e.expenseDate = "Date is required.";
  return e;
}

// ── PDF report ────────────────────────────────────────────────────────────────
async function downloadExpensesPDF(expenses, filters) {
  const { jsPDF }  = await import("jspdf");
  const autoTable   = (await import("jspdf-autotable")).default;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const now = new Date().toLocaleString("en-IN");

  doc.setFontSize(16);
  doc.text("Expense Report", 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${now}`, 14, 25);
  doc.text(`Category: ${filters.category || "All"} | Status: ${filters.status || "All"} | Records: ${expenses.length}`, 14, 30);

  const total = expenses.reduce((s, e) => s + Number(e.amount ?? 0), 0);
  doc.setTextColor(0);
  doc.setFontSize(11);
  doc.text(`Total Expenses: ₹${total.toLocaleString("en-IN")}`, 14, 36);

  const fmt = (v) => v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtAudit = (v) => v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  autoTable(doc, {
    startY: 42,
    head: [[
      "#", "Title", "Category", "Amount (₹)", "Date", "Vendor", "Invoice Ref",
      "Payment Method", "Status", "Description",
      "Recorded By", "Recorded On", "Updated By", "Updated On",
    ]],
    body: expenses.map((ex, i) => [
      i + 1,
      ex.title ?? "",
      ex.category ?? "",
      Number(ex.amount ?? 0).toLocaleString("en-IN"),
      fmt(ex.expenseDate),
      ex.vendor ?? "—",
      ex.invoiceRef ?? "—",
      ex.paymentMethod ?? "—",
      ex.status ?? "",
      ex.description ?? "—",
      ex.createdBy  ?? ex.CreatedBy  ?? "—",
      fmtAudit(ex.createdAt ?? ex.CreatedAt),
      ex.updatedBy  ?? ex.UpdatedBy  ?? "—",
      fmtAudit(ex.updatedAt ?? ex.UpdatedAt),
    ]),
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [211, 47, 47], textColor: 255, fontStyle: "bold", fontSize: 7 },
    alternateRowStyles: { fillColor: [255, 248, 248] },
    columnStyles: {
      10: { textColor: [120, 120, 120] },
      11: { textColor: [120, 120, 120] },
      12: { textColor: [120, 120, 120] },
      13: { textColor: [120, 120, 120] },
    },
  });

  const finalY = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text("Columns 11–14 contain audit trail data. This report is confidential.", 14, finalY);

  doc.save(`expenses-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Expense row ───────────────────────────────────────────────────────────────
function ExpenseRow({ expense, onEdit, onDelete, onApprove }) {
  const canApprove = expense.status === "Pending";
  const canDelete  = expense.status === "Draft" || expense.status === "Rejected";

  return (
    <SoftBox display="flex" alignItems="center" py={1.5} px={2}
      sx={{ borderBottom: "1px solid #f5f5f5", "&:hover": { backgroundColor: "rgba(211,47,47,0.02)" } }}>
      {/* Category icon */}
      <SoftBox width={36} height={36} borderRadius="md" display="flex" alignItems="center" justifyContent="center"
        sx={{ backgroundColor: "rgba(211,47,47,0.08)", flexShrink: 0, mr: 1.5 }}>
        <Icon sx={{ fontSize: "1rem", color: "#d32f2f" }}>{CATEGORY_ICON[expense.category] ?? "receipt_long"}</Icon>
      </SoftBox>

      <SoftBox flex={1} minWidth={0}>
        <SoftTypography variant="button" fontWeight="medium" color="dark" display="block" lineHeight={1.3}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {expense.title}
        </SoftTypography>
        <SoftTypography variant="caption" color="text">
          {expense.category}
          {expense.vendor ? ` · ${expense.vendor}` : ""}
        </SoftTypography>
      </SoftBox>

      <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 100px", textAlign: "center" }}>
        {expense.expenseDate ? new Date(expense.expenseDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
      </SoftTypography>

      <SoftTypography variant="button" fontWeight="bold"
        sx={{ flex: "0 0 110px", textAlign: "right", color: "#d32f2f" }}>
        ₹{Number(expense.amount ?? 0).toLocaleString("en-IN")}
      </SoftTypography>

      <SoftBox sx={{ flex: "0 0 100px", textAlign: "center" }}>
        <SoftBadge badgeContent={expense.status} color={STATUS_COLOR[expense.status] ?? "secondary"} size="xs" container />
      </SoftBox>

      <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 120px" }}>
        {expense.paymentMethod ?? "—"}
        {expense.invoiceRef ? ` · ${expense.invoiceRef}` : ""}
      </SoftTypography>

      <SoftBox display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
        {canApprove && (
          <IconButton size="small" onClick={() => onApprove(expense.id)} sx={{ width: 28, height: 28 }}
            title="Approve">
            <Icon sx={{ fontSize: "0.9rem", color: "#2e7d32" }}>check_circle</Icon>
          </IconButton>
        )}
        <IconButton size="small" onClick={() => onEdit(expense)} sx={{ width: 28, height: 28 }}>
          <Icon sx={{ fontSize: "0.9rem", color: "#1a73e8" }}>edit</Icon>
        </IconButton>
        {canDelete && (
          <IconButton size="small" onClick={() => onDelete(expense.id)} sx={{ width: 28, height: 28 }}>
            <Icon sx={{ fontSize: "0.9rem", color: "#d32f2f" }}>delete</Icon>
          </IconButton>
        )}
      </SoftBox>
    </SoftBox>
  );
}
ExpenseRow.propTypes = {
  expense: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
};

// ── Main ──────────────────────────────────────────────────────────────────────
function ExpensesPage() {
  const [expenses, setExpenses]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus]     = useState("");
  const [filterMonth, setFilterMonth]       = useState(currentMonth);
  const [filterYear, setFilterYear]         = useState(currentYear);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError]     = useState("");
  const [saving, setSaving]           = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    financeService.getExpenses({
      category: filterCategory || undefined,
      status:   filterStatus   || undefined,
      month:    filterMonth,
      year:     filterYear,
    })
      .then((d) => setExpenses(Array.isArray(d) ? d : []))
      .catch(() => setExpenses([]))
      .finally(() => setLoading(false));
  }, [filterCategory, filterStatus, filterMonth, filterYear]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null); setForm(emptyForm); setFieldErrors({}); setSaveError("");
    setDialogOpen(true);
  };

  const openEdit = (expense) => {
    setEditing(expense);
    setForm({
      title:         expense.title ?? "",
      category:      expense.category ?? "Equipment",
      amount:        String(expense.amount ?? ""),
      paymentMethod: expense.paymentMethod ?? "Bank Transfer",
      expenseDate:   expense.expenseDate ? String(expense.expenseDate).slice(0, 10) : "",
      vendor:        expense.vendor ?? "",
      invoiceRef:    expense.invoiceRef ?? "",
      description:   expense.description ?? "",
      status:        expense.status ?? "Draft",
    });
    setFieldErrors({}); setSaveError(""); setDialogOpen(true);
  };

  const f = (key) => (e) => {
    setForm((p) => ({ ...p, [key]: e.target.value }));
    setFieldErrors((p) => ({ ...p, [key]: undefined }));
  };

  const handleSave = async () => {
    const errs = validateExpense(form);
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); setSaveError("Fix highlighted errors."); return; }
    setSaving(true); setSaveError("");
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editing) await financeService.updateExpense(editing.id, payload);
      else         await financeService.createExpense(payload);
      setDialogOpen(false); load();
    } catch (e) {
      setSaveError(e?.detail ?? e?.title ?? "Save failed.");
    } finally { setSaving(false); }
  };

  const handleApprove = async (id) => {
    try { await financeService.approveExpense(id); load(); } catch { /* noop */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await financeService.deleteExpense(id); load(); } catch { /* noop */ }
  };

  const totalAmount   = expenses.reduce((s, e) => s + Number(e.amount ?? 0), 0);
  const approved      = expenses.filter((e) => e.status === "Approved" || e.status === "Paid");
  const approvedAmt   = approved.reduce((s, e) => s + Number(e.amount ?? 0), 0);
  const pending       = expenses.filter((e) => e.status === "Pending").length;
  const draft         = expenses.filter((e) => e.status === "Draft").length;

  // Category breakdown chips
  const categoryBreakdown = CATEGORIES.reduce((acc, cat) => {
    const catExpenses = expenses.filter((e) => e.category === cat);
    if (catExpenses.length > 0)
      acc.push({ cat, total: catExpenses.reduce((s, e) => s + Number(e.amount ?? 0), 0) });
    return acc;
  }, []).sort((a, b) => b.total - a.total);

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Expense Management</SoftTypography>
            <SoftTypography variant="body2" color="text">Track and approve club expenses by category</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            <SoftButton variant="outlined" color="error" size="small"
              startIcon={<Icon>download</Icon>}
              disabled={expenses.length === 0}
              onClick={() => downloadExpensesPDF(expenses, { category: filterCategory, status: filterStatus })}>
              Export PDF
            </SoftButton>
            <SoftButton variant="gradient" color="error" startIcon={<Icon>add</Icon>} onClick={openAdd}>
              Add Expense
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {/* KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Expenses" }}
                count={`₹${Math.round(totalAmount / 1000)}K`}
                percentage={{ color: "error", text: `${expenses.length} records` }}
                icon={{ color: "error", component: "receipt_long" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Approved" }}
                count={`₹${Math.round(approvedAmt / 1000)}K`}
                percentage={{ color: "success", text: `${approved.length} approved` }}
                icon={{ color: "success", component: "check_circle" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Pending Approval" }}
                count={String(pending)}
                percentage={{ color: "warning", text: "awaiting review" }}
                icon={{ color: "warning", component: "pending_actions" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Draft" }}
                count={String(draft)}
                percentage={{ color: "secondary", text: "not submitted" }}
                icon={{ color: "secondary", component: "edit_note" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Category breakdown */}
        {categoryBreakdown.length > 0 && (
          <SoftBox mb={3}>
            <Card>
              <SoftBox p={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ mr: 1 }}>BY CATEGORY:</SoftTypography>
                {categoryBreakdown.map(({ cat, total }) => (
                  <Chip key={cat}
                    icon={<Icon sx={{ fontSize: "0.9rem !important" }}>{CATEGORY_ICON[cat] ?? "receipt"}</Icon>}
                    label={`${cat} · ₹${Math.round(total / 1000)}K`}
                    size="small"
                    onClick={() => setFilterCategory(filterCategory === cat ? "" : cat)}
                    sx={{
                      fontWeight: filterCategory === cat ? 700 : 500,
                      backgroundColor: filterCategory === cat ? "rgba(211,47,47,0.12)" : "rgba(0,0,0,0.04)",
                      color: "#344767",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </SoftBox>
            </Card>
          </SoftBox>
        )}

        {/* Table */}
        <Card>
          {/* Filters */}
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center"
            sx={{ borderBottom: "1px solid #f0f0f0" }}>
            <TextField select size="small" label="Month" value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)} sx={{ minWidth: 110 }}>
              {MONTHS.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Year" value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)} sx={{ minWidth: 90 }}>
              {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Category" value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">All Categories</MenuItem>
              {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="◈ Status" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">◈ All Status</MenuItem>
              <MenuItem value="Draft">◆ Draft</MenuItem>
              <MenuItem value="Pending">▶ Pending</MenuItem>
              <MenuItem value="Approved">▲ Approved</MenuItem>
              <MenuItem value="Rejected">▼ Rejected</MenuItem>
              <MenuItem value="Paid">✓ Paid</MenuItem>
            </TextField>
            <SoftTypography variant="caption" color="text" sx={{ ml: "auto" }}>
              {expenses.length} records · ₹{totalAmount.toLocaleString("en-IN")} total
            </SoftTypography>
          </SoftBox>

          {/* Column headers */}
          <SoftBox display="flex" alignItems="center" px={2} py={1}
            sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa" }}>
            <SoftBox sx={{ width: 50 }} />
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>EXPENSE</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 100px", textAlign: "center" }}>DATE</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 110px", textAlign: "right" }}>AMOUNT</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 100px", textAlign: "center" }}>STATUS</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 120px" }}>PAYMENT / REF</SoftTypography>
            <SoftBox sx={{ width: 90 }} />
          </SoftBox>

          {loading ? (
            <FootballLoader label="Loading expenses…" />
          ) : expenses.length === 0 ? (
            <SoftBox p={4} textAlign="center">
              <Icon sx={{ fontSize: "2rem", color: "#bbb", display: "block", mb: 1 }}>receipt_long</Icon>
              <SoftTypography variant="caption" color="text">No expenses found for the selected filters</SoftTypography>
            </SoftBox>
          ) : (
            expenses.map((ex) => (
              <ExpenseRow key={ex.id} expense={ex}
                onEdit={openEdit} onDelete={handleDelete} onApprove={handleApprove} />
            ))
          )}

          {/* Footer */}
          {expenses.length > 0 && (
            <SoftBox display="flex" alignItems="center" px={2} py={1.5}
              sx={{ borderTop: "2px solid #f0f0f0", backgroundColor: "#fafafa" }}>
              <SoftBox sx={{ width: 50 }} />
              <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={{ flex: 1 }}>
                TOTAL ({expenses.length} records)
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 100px" }} />
              <SoftTypography variant="caption" fontWeight="bold" sx={{ flex: "0 0 110px", textAlign: "right", color: "#d32f2f" }}>
                ₹{totalAmount.toLocaleString("en-IN")}
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 100px", textAlign: "center" }}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                  {approved.length} approved
                </SoftTypography>
              </SoftBox>
              <SoftBox sx={{ flex: "0 0 210px" }} />
            </SoftBox>
          )}
        </Card>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* ── Add / Edit dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { maxHeight: "90vh" } }}>
        <DialogTitle>{editing ? "Edit Expense" : "Add Expense"}</DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Title / Description" value={form.title}
                onChange={f("title")} size="small"
                error={Boolean(fieldErrors.title)} helperText={fieldErrors.title}
                placeholder="e.g. Monthly Electricity Bill — Main Ground" />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth required label="Category" value={form.category}
                onChange={f("category")} size="small"
                error={Boolean(fieldErrors.category)} helperText={fieldErrors.category}>
                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Amount (₹)" type="number" value={form.amount}
                onChange={f("amount")} size="small"
                error={Boolean(fieldErrors.amount)} helperText={fieldErrors.amount}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Expense Date" type="date" value={form.expenseDate}
                onChange={f("expenseDate")} size="small" InputLabelProps={{ shrink: true }}
                error={Boolean(fieldErrors.expenseDate)} helperText={fieldErrors.expenseDate} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Payment Method" value={form.paymentMethod}
                onChange={f("paymentMethod")} size="small">
                {PAYMENT_METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Vendor / Payee" value={form.vendor}
                onChange={f("vendor")} size="small" placeholder="e.g. Sports Authority Ltd" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Invoice / Reference #" value={form.invoiceRef}
                onChange={f("invoiceRef")} size="small" placeholder="INV-2025-001" />
            </Grid>
            {editing && (
              <Grid item xs={12}>
                <TextField select fullWidth label="Status" value={form.status}
                  onChange={f("status")} size="small">
                  {["Draft", "Pending", "Approved", "Rejected", "Paid"].map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label="Additional Notes" value={form.description}
                onChange={f("description")} size="small" multiline rows={2}
                placeholder="Optional details about this expense" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="error" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editing ? "Update" : "Add Expense"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default ExpensesPage;
